// src/lib/themes.ts -- Slice J / Phase 2, extended 2026-11-04 (Dark Mode).
//
// User-selectable skin definitions. Three themes:
//   * "warm-gold"    -- the Info Hub heritage palette (default; no visual
//                       change for users who keep it).
//   * "nymr-website" -- palette tokens extracted from the live nymr.co.uk
//                       site (compiled Bootstrap `app/brands/default/app.css`).
//   * "pale-blue"    -- the heritage layout on cool pale-blue surfaces with a
//                       navy accent (tone selector, 2026-11-04).
//   * "pale-green"   -- the same on pale-green surfaces with a moss accent.
//   * "dark"         -- Dark Mode, styled like the iPhone system dark mode:
//                       near-black background, grouped cards in dark grey,
//                       system-blue accents, light text.
//
// Every token below is a CSS custom property that src/theme.ts reads through
// `var(--c-…, <warm-gold fallback>)`. useTheme publishes the chosen set on
// document.documentElement, so components that only use the C tokens re-skin
// without changes. Values MUST be literal colours (never `var(...)`) -- a
// token that references itself is a cyclic custom property and falls back
// silently.
//
// Source (NYMR Website palette):
//   Extracted 2026-05-18 from https://www.nymr.co.uk/app/brands/default/app.css
//   The live site uses Bootstrap 5 with custom --bs-* overrides. The core
//   tokens map to: --bs-primary, --bs-secondary, --bs-body-bg,
//   --bs-body-color, --bs-link-color. The site's body font (Figtree) is
//   intentionally NOT migrated -- Slice J is colour-only.

export type ThemeKey = "warm-gold" | "pale-blue" | "pale-green" | "nymr-website" | "dark";

export interface ThemeTokens {
  /** "light" | "dark" -- published as the CSS `color-scheme` so native form
   *  controls, scrollbars and the browser chrome follow the skin. */
  scheme: "light" | "dark";
  accent: string;
  accentBg: string;
  accentLt: string;
  accentGlow: string;
  accentSurf: string;
  bg: string;
  paperWarm: string;
  paperSoft: string;
  card: string;
  ink: string;
  inkSoft: string;
  inkMute: string;
  link: string;
  hairline: string;
  hairlineLt: string;
  heatherDk: string;
  heatherBg: string;
  sageDk: string;
  sageBg: string;
  signalDk: string;
  signalBg: string;
  shadowS: string;
  shadowM: string;
  shadowL: string;
  /** Desktop scrollbar thumb / track (see index.css). */
  scrollThumb: string;
  scrollTrack: string;
}

const WARM_GOLD: ThemeTokens = {
  scheme: "light",
  accent:     "#A6771C",
  accentBg:   "#FBEFCB",
  accentLt:   "#D9B466",
  accentGlow: "#F4E2A8",
  accentSurf: "#FFF6DC",
  bg:         "#FBF6E8",
  paperWarm:  "#F5EBD3",
  paperSoft:  "#FFFDF6",
  card:       "#FFFFFF",
  ink:        "#2B2014",
  inkSoft:    "#604C32",
  inkMute:    "#9F8B6B",
  link:       "#7A540B",
  hairline:   "#E8DCBC",
  hairlineLt: "#F0E7D0",
  heatherDk:  "#56406A",
  heatherBg:  "#EBE0F0",
  sageDk:     "#4F6852",
  sageBg:     "#E4ECDF",
  signalDk:   "#8A2A1F",
  signalBg:   "#F8D9D2",
  shadowS: "0 1px 0 rgba(122,84,11,0.04), 0 2px 6px rgba(122,84,11,0.06)",
  shadowM: "0 1px 0 rgba(122,84,11,0.04), 0 8px 22px rgba(122,84,11,0.10)",
  shadowL: "0 2px 0 rgba(122,84,11,0.05), 0 22px 50px rgba(122,84,11,0.16)",
  scrollThumb: "rgba(122,84,11,0.55)",
  scrollTrack: "rgba(122,84,11,0.08)",
};

export const THEMES: Readonly<Record<ThemeKey, ThemeTokens>> = {
  "warm-gold": WARM_GOLD,
  // Tone selector (2026-11-04): same heritage layout, cool pale-blue surfaces,
  // navy-blue accent in place of brass.
  "pale-blue": {
    ...WARM_GOLD,
    accent:     "#2F5F9E",
    accentBg:   "#DCE8F7",
    accentLt:   "#8FB3E0",
    accentGlow: "#C9DCF4",
    accentSurf: "#EEF4FC",
    bg:         "#EEF4FA",
    paperWarm:  "#DFE9F4",
    paperSoft:  "#F7FAFE",
    ink:        "#15243A",
    inkSoft:    "#3F526C",
    inkMute:    "#7E8FA6",
    link:       "#234C84",
    hairline:   "#CCDAEA",
    hairlineLt: "#DEE8F3",
    shadowS: "0 1px 0 rgba(35,76,132,0.04), 0 2px 6px rgba(35,76,132,0.07)",
    shadowM: "0 1px 0 rgba(35,76,132,0.04), 0 8px 22px rgba(35,76,132,0.10)",
    shadowL: "0 2px 0 rgba(35,76,132,0.05), 0 22px 50px rgba(35,76,132,0.16)",
    scrollThumb: "rgba(35,76,132,0.5)",
    scrollTrack: "rgba(35,76,132,0.08)",
  },
  // Tone selector (2026-11-04): pale-green surfaces, moss accent.
  "pale-green": {
    ...WARM_GOLD,
    accent:     "#4E7D55",
    accentBg:   "#DCEEDD",
    accentLt:   "#9CC4A1",
    accentGlow: "#CDE5CF",
    accentSurf: "#EFF7EF",
    bg:         "#EFF6EE",
    paperWarm:  "#DFEBDD",
    paperSoft:  "#F8FCF7",
    ink:        "#1A2A1C",
    inkSoft:    "#425447",
    inkMute:    "#7F9283",
    link:       "#36603C",
    hairline:   "#CBDCCB",
    hairlineLt: "#DEEADD",
    shadowS: "0 1px 0 rgba(54,96,60,0.04), 0 2px 6px rgba(54,96,60,0.07)",
    shadowM: "0 1px 0 rgba(54,96,60,0.04), 0 8px 22px rgba(54,96,60,0.10)",
    shadowL: "0 2px 0 rgba(54,96,60,0.05), 0 22px 50px rgba(54,96,60,0.16)",
    scrollThumb: "rgba(54,96,60,0.5)",
    scrollTrack: "rgba(54,96,60,0.08)",
  },
  "nymr-website": {
    ...WARM_GOLD,
    accent:   "#fd9c00",  // --bs-primary; orange/amber
    accentBg: "#003d1f",  // --bs-secondary; deep heritage green (headers/dark surfaces)
    bg:       "#e8e0d4",  // --bs-body-bg; warm cream / parchment
    ink:      "#00282a",  // --bs-body-color; near-black with green tint
    link:     "#da4c2d",  // --bs-link-color; terracotta
  },
  // iPhone system dark mode: systemBackground #000, secondarySystemGroupedBackground
  // #1C1C1E, tertiary #2C2C2E, separator #38383A, label #FFF, secondaryLabel
  // ~#EBEBF5 @ 60%, systemBlue (dark) #0A84FF.
  dark: {
    scheme: "dark",
    accent:     "#0A84FF",
    accentBg:   "#0E2A4A",
    accentLt:   "#3D9BFF",
    accentGlow: "#9CCBFF",
    accentSurf: "#15202E",
    bg:         "#000000",
    paperWarm:  "#2C2C2E",
    paperSoft:  "#161618",
    card:       "#1C1C1E",
    ink:        "#FFFFFF",
    inkSoft:    "#C7C7CC",
    inkMute:    "#8E8E93",
    link:       "#409CFF",
    hairline:   "#38383A",
    hairlineLt: "#2C2C2E",
    heatherDk:  "#CDA9E6",
    heatherBg:  "#2A2340",
    sageDk:     "#9CD4A6",
    sageBg:     "#1D2E22",
    signalDk:   "#FF8A80",
    signalBg:   "#3A1F1C",
    shadowS: "0 1px 0 rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.55)",
    shadowM: "0 1px 0 rgba(0,0,0,0.5), 0 8px 22px rgba(0,0,0,0.6)",
    shadowL: "0 2px 0 rgba(0,0,0,0.5), 0 22px 50px rgba(0,0,0,0.7)",
    scrollThumb: "rgba(235,235,245,0.38)",
    scrollTrack: "rgba(235,235,245,0.06)",
  },
};

export const THEME_KEYS: ReadonlyArray<ThemeKey> = ["warm-gold", "pale-blue", "pale-green", "nymr-website", "dark"];

export const THEME_LABELS: Readonly<Record<ThemeKey, string>> = {
  "warm-gold":    "Pale Yellow",
  "pale-blue":    "Pale Blue",
  "pale-green":   "Pale Green",
  "nymr-website": "NYMR Website",
  dark:           "Dark Mode",
};

export const DEFAULT_THEME: ThemeKey = "warm-gold";

// CSS custom-property names. Components consume these via var(--c-…) fallbacks
// in src/theme.ts; useTheme writes them on document.documentElement.
export const THEME_CSS_VARS: Readonly<Record<Exclude<keyof ThemeTokens, "scheme">, string>> = {
  accent:     "--c-accent",
  accentBg:   "--c-accent-bg",
  accentLt:   "--c-accent-lt",
  accentGlow: "--c-accent-glow",
  accentSurf: "--c-accent-surf",
  bg:         "--c-bg",
  paperWarm:  "--c-paper-warm",
  paperSoft:  "--c-paper-soft",
  card:       "--c-card",
  ink:        "--c-ink",
  inkSoft:    "--c-ink-soft",
  inkMute:    "--c-ink-mute",
  link:       "--c-link",
  hairline:   "--c-hairline",
  hairlineLt: "--c-hairline-lt",
  heatherDk:  "--c-heather-dk",
  heatherBg:  "--c-heather-bg",
  sageDk:     "--c-sage-dk",
  sageBg:     "--c-sage-bg",
  signalDk:   "--c-signal-dk",
  signalBg:   "--c-signal-bg",
  shadowS:    "--c-shadow-s",
  shadowM:    "--c-shadow-m",
  shadowL:    "--c-shadow-l",
  scrollThumb: "--c-scroll-thumb",
  scrollTrack: "--c-scroll-track",
};

export function getTheme(key: string | null | undefined): ThemeTokens {
  if (key && key in THEMES) return THEMES[key as ThemeKey];
  return THEMES[DEFAULT_THEME];
}

export function isThemeKey(v: unknown): v is ThemeKey {
  return typeof v === "string" && (THEME_KEYS as ReadonlyArray<string>).includes(v);
}

// The last applied skin is remembered locally so the next page load paints in
// the right palette before /api/me answers (no white flash for Dark Mode users).
const THEME_CACHE_KEY = "nymr_theme";

export function readCachedTheme(): ThemeKey | null {
  try {
    const v = localStorage.getItem(THEME_CACHE_KEY);
    return isThemeKey(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeCachedTheme(key: ThemeKey): void {
  try { localStorage.setItem(THEME_CACHE_KEY, key); } catch { /* private mode */ }
}

/** Publish a skin's tokens on the root element (+ body background, colour
 *  scheme and the browser theme-color). Idempotent; safe to call often. */
export function applyThemeToDocument(key: ThemeKey): void {
  if (typeof document === "undefined") return;
  const tokens = getTheme(key);
  const root = document.documentElement;
  (Object.keys(THEME_CSS_VARS) as Array<keyof typeof THEME_CSS_VARS>).forEach((tokenKey) => {
    root.style.setProperty(THEME_CSS_VARS[tokenKey], tokens[tokenKey]);
  });
  root.style.colorScheme = tokens.scheme;
  root.dataset.theme = key;
  root.style.background = tokens.bg;
  document.body.style.background = tokens.bg;
  document.body.style.color = tokens.ink;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = tokens.scheme === "dark" ? tokens.card : tokens.accent;
}
